import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

const CART_DRAWER_CONTENT_UPDATED_EVENT = 'cart-drawer:content-updated';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    150
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /** Number of cart requests currently waiting for Shopify. */
  #pendingUpdateCount = 0;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.addEventListener('submit', this.#syncReorderedFormQuantities);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.removeEventListener('submit', this.#syncReorderedFormQuantities);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove.
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Preserve Horizon's original immediate empty-cart transition.
    const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;
    const template = document.getElementById('empty-cart-template');

    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Preserve Horizon's original row-collapse removal animation.
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems(config.action === 'clear');

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.#getCartItemRow(line)?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update the displayed total directly from Shopify's fresh section
        // response before the larger drawer morph runs. This keeps the total
        // reliable even when a custom drawer layout reorders cart rows.
        this.#updateCartTotal(newSectionHTML);

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();

        if (this.isDrawer) {
          document.dispatchEvent(new CustomEvent(CART_DRAWER_CONTENT_UPDATED_EVENT));
        }

        // Resolve only after the new rows are in the DOM. Opening or measuring
        // the drawer before this point leaves mobile browsers with stale height.
        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors.find(
      (selector) => selector.querySelector('input')?.dataset.cartLine === String(line)
    ) ?? this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            await startViewTransition(() => morphSection(this.sectionId, cartItemsHtml, morphOptions), [
              'fill-cart-drawer',
            ]);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();

          if (this.isDrawer) {
            document.dispatchEvent(new CustomEvent(CART_DRAWER_CONTENT_UPDATED_EVENT));
          }
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
          if (this.isDrawer) {
            document.dispatchEvent(new CustomEvent(CART_DRAWER_CONTENT_UPDATED_EVENT));
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Finds a displayed cart row by its original Shopify line number.
   * Drawer rows can be visually regrouped without changing cart mutation targets.
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRow(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.line === String(line)) ?? this.refs.cartItemRows[line - 1];
  }

  /**
   * Copies visible reordered quantities into Shopify's original-order form inputs
   * immediately before checkout or another native cart form submission.
   * @param {SubmitEvent} event
   */
  #syncReorderedFormQuantities = (event) => {
    if (!(event.target instanceof HTMLFormElement) || event.target.id !== 'cart-form') return;

    const originalOrderInputs = event.target.querySelectorAll('input[data-original-cart-line]');
    for (const originalInput of originalOrderInputs) {
      const line = originalInput.getAttribute('data-original-cart-line');
      const visibleInput = event.target.querySelector(`input[data-cart-line="${line}"]`);
      if (visibleInput) originalInput.value = visibleInput.value;
    }
  };

  /**
   * Immediately syncs the visible cart total from a freshly rendered Shopify
   * section. The subsequent section morph still updates the rest of the drawer.
   * @param {Document} newSectionHTML
   */
  #updateCartTotal(newSectionHTML) {
    const currentTotal = this.querySelector('[ref="cartTotal"]');
    const nextTotal = newSectionHTML.querySelector('[ref="cartTotal"]');

    if (!(currentTotal instanceof HTMLElement) || !(nextTotal instanceof HTMLElement)) return;

    const nextValue = nextTotal.getAttribute('value');
    if (nextValue !== null) currentTotal.setAttribute('value', nextValue);

    currentTotal.innerHTML = nextTotal.innerHTML;
    currentTotal.removeAttribute('shimmer');
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems(isRemoval = false) {
    this.#pendingUpdateCount += 1;
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');

    if (this.isDrawer && isRemoval) {
      const drawerDialog = this.closest('dialog') ?? document.querySelector('#cart-drawer dialog');
      drawerDialog?.setAttribute('data-cart-removing', '');
    }
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.#pendingUpdateCount = Math.max(0, this.#pendingUpdateCount - 1);
    if (this.#pendingUpdateCount > 0) return;

    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
    if (this.isDrawer) {
      const drawerDialog = this.closest('dialog') ?? document.querySelector('#cart-drawer dialog');
      drawerDialog?.removeAttribute('data-cart-removing');
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

/**
 * Free-shipping progress for the cart drawer.
 *
 * The Theme Editor threshold is stored in the shop's base currency. Shopify's
 * active Markets conversion rate converts it to the cart's presentment
 * currency before it is compared with cart.total_price.
 */
class CartFreeShippingProgress extends HTMLElement {
  static observedAttributes = [
    'data-threshold-base',
    'data-cart-total',
    'data-progress-text',
    'data-success-text',
    'data-currency-code',
    'data-locale',
  ];

  #renderQueued = false;

  connectedCallback() {
    this.#scheduleRender();
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#scheduleRender();
  }

  #scheduleRender() {
    if (this.#renderQueued) return;

    this.#renderQueued = true;
    queueMicrotask(() => {
      this.#renderQueued = false;
      if (this.isConnected) this.render();
    });
  }

  render() {
    const baseThreshold = Number(this.dataset.thresholdBase || 0);
    const cartTotal = Number(this.dataset.cartTotal || 0);
    const marketRate = Number(window.Shopify?.currency?.rate || 1);
    const threshold = Math.max(0, Math.round(baseThreshold * marketRate));
    const remaining = Math.max(0, threshold - cartTotal);
    const complete = threshold <= 0 || remaining <= 0;
    const progress = threshold <= 0 ? 100 : Math.min(100, Math.max(0, (cartTotal / threshold) * 100));

    const message = this.querySelector('[data-shipping-message]');
    const track = this.querySelector('.cart-drawer__shipping-track');
    const fill = this.querySelector('[data-shipping-fill]');

    if (message) {
      const template = complete ? this.dataset.successText || '' : this.dataset.progressText || '';
      message.textContent = template.replace(/\[amount\]/gi, this.#formatMoney(remaining));
    }

    if (track) track.setAttribute('aria-valuenow', String(Math.round(progress)));
    if (fill instanceof HTMLElement) fill.style.width = `${progress}%`;

    // Keep the shipping block visible after the threshold is reached.
    // Avoid the generic [complete] attribute because theme/app CSS can use it
    // to hide completed progress components.
    this.removeAttribute('complete');
    this.dataset.status = complete ? 'complete' : 'progress';
    this.setAttribute('ready', '');
  }

  /**
   * Formats Shopify's integer subunit amount in the active cart currency.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const currency = this.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';
    const locale = this.dataset.locale || document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }
}

if (!customElements.get('cart-free-shipping-progress')) {
  customElements.define('cart-free-shipping-progress', CartFreeShippingProgress);
}